import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";

// Site::Lp02to04::AreaCta::Component の Stimulus コントローラ。
// landIeul: AreaCta の 土地の有無 → 都道府県 → 市区町村 → 町名 → 字・丁目 の 5 段階選択を再現。
// 市区町村/町名/字丁目は GraphQL で取得し optgroup を groupByYomi で構築する。
// current/done クラスの prefix は data-...-prefix-value で受け取る (va2 = Lp02to04va2 / default = Lp05va2)。

interface NamedItem {
  id: string;
  name: string;
  yomi: string;
}

const GROUP_MAP: [RegExp, string][] = [
  [/^[ア-オ]/, "ア"],
  [/^[カ-コガ-ゴ]/, "カ"],
  [/^[サ-ソザ-ゾ]/, "サ"],
  [/^[タ-トダ-ド]/, "タ"],
  [/^[ナ-ノ]/, "ナ"],
  [/^[ハ-ホバ-ボパ-ポ]/, "ハ"],
  [/^[マ-モ]/, "マ"],
  [/^[ヤ-ヨ]/, "ヤ"],
  [/^[ラ-ロ]/, "ラ"],
  [/^[ワ-ン]/, "ワ"],
];

function mapToGroup(yomi: string): string {
  const m = GROUP_MAP.find(([re]) => re.test(yomi));
  return m ? m[1] : "その他";
}

function groupByYomi(items: NamedItem[]): { name: string; items: NamedItem[] }[] {
  const map = new Map<string, NamedItem[]>();
  items.forEach((it) => {
    const g = mapToGroup(it.yomi);
    map.set(g, [...(map.get(g) || []), it]);
  });
  return Array.from(map.keys())
    .sort()
    .map((k) => ({
      name: k,
      items: (map.get(k) || []).sort((a, b) =>
        a.yomi < b.yomi ? -1 : a.yomi > b.yomi ? 1 : 0,
      ),
    }));
}

const GET_CITIES = `query GetCities($id: ID!){prefecture(id:$id){cities{id name yomi}}}`;
const GET_TOWNS = `query GetTowns($id: ID!){city(id:$id){towns{id name yomi}}}`;
const GET_STREETS = `query GetStreets($id: ID!){town(id:$id){streets{id name yomi}}}`;

const STREET_FALLBACK: NamedItem[] = [{ id: "0", name: "その他", yomi: "その他" }];

class SiteLp02to04AreaCtaController extends Controller {
  static targets = [
    "possession",
    "prefecture",
    "city",
    "town",
    "street",
    "group",
    "button",
    "link",
  ];
  static values = { graphql: String, prefix: String, base: String };

  declare readonly possessionTarget: HTMLSelectElement;
  declare readonly prefectureTarget: HTMLSelectElement;
  declare readonly cityTarget: HTMLSelectElement;
  declare readonly townTarget: HTMLSelectElement;
  declare readonly streetTarget: HTMLSelectElement;
  declare readonly groupTargets: HTMLElement[];
  declare readonly linkTarget: HTMLAnchorElement;
  declare readonly graphqlValue: string;
  declare readonly prefixValue: string;
  declare readonly baseValue: string;

  private readonly LEVELS = ["possession", "prefecture", "city", "town", "street"];

  async change(event: Event): Promise<void> {
    const level = this.levelOf(event.target as HTMLSelectElement);
    if (level === "possession" && this.possessionTarget.value === "") {
      this.reset();
    } else if (level === "prefecture") {
      await this.reloadCities();
      this.resetTowns();
      this.resetStreets();
    } else if (level === "city") {
      await this.reloadTowns();
      this.resetStreets();
    } else if (level === "town") {
      await this.reloadStreets();
    }
    this.refresh();
  }

  private levelOf(select: HTMLSelectElement): string {
    if (select === this.prefectureTarget) return "prefecture";
    if (select === this.cityTarget) return "city";
    if (select === this.townTarget) return "town";
    if (select === this.streetTarget) return "street";
    return "possession";
  }

  private async fetchData(query: string, id: string): Promise<any> {
    const res = await fetch(this.graphqlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, variables: { id } }),
    });
    const json = await res.json();
    return json.data;
  }

  private optionsHtml(
    placeholder: string,
    groups: { name: string; items: NamedItem[] }[],
  ): string {
    let html = `<option value="">${placeholder}</option>`;
    groups.forEach((g) => {
      html += `<optgroup label="${g.name}">`;
      g.items.forEach((it) => {
        html += `<option value="${it.id}">${it.name}</option>`;
      });
      html += "</optgroup>";
    });
    return html;
  }

  private async reloadCities(): Promise<void> {
    const pref = this.prefectureTarget.value;
    if (!pref) {
      this.cityTarget.innerHTML = '<option value="">市区町村</option>';
      return;
    }
    const data = await this.fetchData(GET_CITIES, pref);
    const cities: NamedItem[] = data?.prefecture?.cities || [];
    this.cityTarget.innerHTML = this.optionsHtml("市区町村", groupByYomi(cities));
  }

  private async reloadTowns(): Promise<void> {
    const city = this.cityTarget.value;
    if (!city) {
      this.resetTowns();
      return;
    }
    const data = await this.fetchData(GET_TOWNS, city);
    const towns: NamedItem[] = data?.city?.towns || [];
    this.townTarget.innerHTML = this.optionsHtml("町名", groupByYomi(towns));
  }

  private async reloadStreets(): Promise<void> {
    const town = this.townTarget.value;
    let streets: NamedItem[] = STREET_FALLBACK;
    if (town) {
      const data = await this.fetchData(GET_STREETS, town);
      const fetched: NamedItem[] = data?.town?.streets || [];
      if (fetched.length > 0) streets = fetched;
    }
    this.streetTarget.innerHTML = this.optionsHtml("字・丁目", groupByYomi(streets));
  }

  private resetTowns(): void {
    this.townTarget.innerHTML = '<option value="">町名</option>';
  }

  private resetStreets(): void {
    this.streetTarget.innerHTML = this.optionsHtml("字・丁目", groupByYomi(STREET_FALLBACK));
  }

  private reset(): void {
    this.prefectureTarget.value = "";
    this.cityTarget.innerHTML = '<option value="">市区町村</option>';
    this.resetTowns();
    this.resetStreets();
  }

  // done/current クラスと CTA リンクを再計算 (group / selectForm 両方に付与)。
  private refresh(): void {
    const CURRENT = `${this.prefixValue}_current`;
    const DONE = `${this.prefixValue}_done`;
    const vals = [
      this.possessionTarget.value,
      this.prefectureTarget.value,
      this.cityTarget.value,
      this.townTarget.value,
      this.streetTarget.value,
    ];
    const activeIdx = vals.findIndex((v) => !v); // 最初の未選択。全選択なら -1

    this.LEVELS.forEach((level, i) => {
      const filled = !!vals[i];
      const isCurrent = i === activeIdx;
      const group = this.groupTargets.find((g) => g.dataset.level === level);
      if (!group) return;
      group.classList.toggle(DONE, filled);
      group.classList.toggle(CURRENT, isCurrent);
      group
        .querySelectorAll<HTMLElement>(`.${this.prefixValue}_selectForm`)
        .forEach((el) => {
          el.classList.toggle(DONE, filled);
          el.classList.toggle(CURRENT, isCurrent);
        });
    });

    const complete = activeIdx === -1;
    this.linkTarget.classList.toggle(CURRENT, complete);
    this.linkTarget.href = this.buildHref(vals);
  }

  private textOf(select: HTMLSelectElement, placeholder: string): string {
    const opt = select.selectedOptions[0];
    if (opt && opt.text && opt.text !== placeholder) return opt.text;
    return "";
  }

  private buildHref(vals: string[]): string {
    const [poss, pref, city, town] = vals;
    const prefName = this.textOf(this.prefectureTarget, "都道府県");
    const cityName = this.textOf(this.cityTarget, "市区町村");
    const townName = this.textOf(this.townTarget, "町名");
    const streetName = this.textOf(this.streetTarget, "字・丁目");
    const tail =
      `property-type-id=land_usage_${poss}&pref-id=${pref}&city-id=${city}` +
      `&town-id=${town}&prefecture_name=${prefName}&city_name=${cityName}` +
      `&town_name=${townName}&street-value=${streetName}&utm_campaign=Lp_area`;
    const base = this.baseValue || "/plan-request";
    const search = window.location.search;
    return search ? `${base}/${search}&${tail}` : `${base}/?${tail}`;
  }
}

application.register("site--lp02to04--area-cta", SiteLp02to04AreaCtaController);
